import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Package } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductTile from '../components/shop/ProductTile';
import { setSeoMetadata } from '../lib/seo';
import { useCart } from '../context/CartContext';
import { useShopCategories, shopCategorySlug } from '../hooks/useShopCategories';
import ShopCategoryMenu from '../components/shop/ShopCategoryMenu';
import { useSiteFeatureFlags } from '../hooks/useSiteFeatureFlags';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * One gift shop category: /shop/category/<slug>.
 *
 * Reached from the GIFT SHOP menu dropdown and the Browse chips on the shop home page.
 * The slug is derived from the category name, so adding a category in the admin is enough.
 */
const ShopCategoryPage = () => {
  const { slug } = useParams();
  const { categories, loading: catsLoading } = useShopCategories();
  const { left_menu_enabled } = useSiteFeatureFlags();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('featured');

  const category = useMemo(
    () => categories.find((c) => c.slug === slug) || null,
    [categories, slug],
  );

  useEffect(() => {
    if (!category) return;
    setSeoMetadata({
      title: category.name,
      description: category.seo_description || `${category.name} from the St John gift shop - island gear shipped from St John, U.S. Virgin Islands.`,
      canonicalPath: `/shop/category/${slug}`,
      ogType: 'website',
    });
  }, [category, slug]);

  useEffect(() => {
    if (catsLoading) return;
    if (!category) { setProducts([]); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/store/products`, { params: { category: category.name, limit: 200 } })
      .then((res) => { if (!cancelled) setProducts(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [category, catsLoading]);

  const sorted = useMemo(() => {
    const list = [...products];
    if (sort === 'price-asc') return list.sort((a, b) => (a.price || 0) - (b.price || 0));
    if (sort === 'price-desc') return list.sort((a, b) => (b.price || 0) - (a.price || 0));
    if (sort === 'newest') return list.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    return list.sort((a, b) => Number(!!b.featured) - Number(!!a.featured));
  }, [products, sort]);

  return (
    <div className="min-h-screen bg-white" data-testid="shop-category-page">
      <Header />

      <section className="pt-28 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4">
          <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-800 hover:text-blue-900" data-testid="shop-category-back">
            <ArrowLeft className="h-4 w-4" /> Gift Shop
          </Link>
          <h1 className="mt-3 text-3xl font-extrabold italic uppercase tracking-tight text-slate-900 sm:text-4xl" data-testid="shop-category-title">
            {category?.name || (catsLoading ? '\u00a0' : 'Category not found')}
          </h1>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
            <span className="text-sm text-slate-500" data-testid="shop-category-count">{sorted.length} product{sorted.length === 1 ? '' : 's'}</span>
            <label className="flex items-center gap-2 text-sm text-slate-500">
              Sort
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="rounded border border-slate-200 bg-white px-2 py-1.5 text-sm font-semibold text-slate-700"
                data-testid="shop-category-sort"
              >
                <option value="featured">Featured first</option>
                <option value="newest">Newest</option>
                <option value="price-asc">Price: low to high</option>
                <option value="price-desc">Price: high to low</option>
              </select>
            </label>
          </div>
        </div>
      </section>

      <section className="mx-auto flex max-w-7xl gap-8 px-4 py-8 pb-20">
        {left_menu_enabled && (
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-24"><ShopCategoryMenu activeSlug={slug} /></div>
          </aside>
        )}
        <div className="min-w-0 flex-1">
        {loading || catsLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>
        ) : sorted.length === 0 ? (
          <div className="py-20 text-center" data-testid="shop-category-empty">
            <Package className="mx-auto h-12 w-12 text-slate-300" />
            <p className="mt-4 text-slate-500">Nothing in this category yet.</p>
            <Link to="/shop/products" className="mt-6 inline-block rounded bg-blue-800 px-6 py-3 font-bold uppercase tracking-wide text-white hover:bg-blue-900">
              Shop all products
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="shop-category-grid">
            {sorted.map((p) => <ProductTile key={p.id} product={p} onAddToCart={addToCart} />)}
          </div>
        )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export { shopCategorySlug };
export default ShopCategoryPage;
